<script setup lang="ts">
import { computed, ref } from 'vue'
import { DateTime } from 'luxon'

import { useCalculationStore } from '@/stores/calculation'
import { OUTPUT_ACCESS_DURATIONS } from '@/stores/type/calculation.type'
import type { OutputAccessDuration, OutputAccessLink } from '@/stores/type/calculation.type'

const props = defineProps<{
  calculationId: number
  path: string[]
}>()
const dialog = defineModel<boolean>({ required: true })
const calculationStore = useCalculationStore()

const duration = ref<OutputAccessDuration>('day')
const link = ref<OutputAccessLink | null>(null)
const loading = ref(false)
const error = ref('')
const copied = ref(false)
const copyError = ref('')

const folderLabel = computed(() => ['Output files', ...props.path].join(' / '))
const expiryLabel = computed(() =>
  link.value ? DateTime.fromISO(link.value.expires_at).toLocaleString(DateTime.DATETIME_FULL) : ''
)

const createLink = async () => {
  if (loading.value) return
  loading.value = true
  error.value = ''
  copyError.value = ''
  copied.value = false
  link.value = null
  try {
    link.value = await calculationStore.createOutputAccessLink(
      props.calculationId,
      duration.value,
      props.path
    )
  } catch (cause) {
    error.value = cause instanceof Error ? cause.message : 'The access link could not be created.'
  } finally {
    loading.value = false
  }
}

const copyLink = async () => {
  if (!link.value) return
  copyError.value = ''
  try {
    await navigator.clipboard.writeText(link.value.access_url)
    copied.value = true
  } catch {
    copyError.value = 'Select the URL above and copy it manually.'
  }
}
</script>

<template>
  <v-dialog v-model="dialog" max-width="640" aria-labelledby="output-access-title">
    <v-card>
      <v-card-title id="output-access-title" class="d-flex align-center pt-5 px-6">
        <v-icon icon="mdi-link-variant" class="mr-3" size="small" />
        Temporary folder access
        <v-spacer />
        <v-btn
          icon="mdi-close"
          aria-label="Close temporary folder access"
          size="small"
          variant="text"
          @click="dialog = false"
        />
      </v-card-title>
      <v-card-text class="px-6">
        <p class="text-body-2 mb-4">
          Create a link to browse and download this folder directly from Azure. All files and
          subfolders are included, with read-only access.
        </p>
        <div class="access-folder mb-5">
          <v-icon icon="mdi-folder-outline" size="small" />
          <span>{{ folderLabel }}</span>
        </div>
        <v-select
          v-if="!link"
          v-model="duration"
          :items="OUTPUT_ACCESS_DURATIONS"
          label="Access expires after"
          :disabled="loading"
          variant="outlined"
          hide-details
          class="mb-4"
        />
        <p v-if="!link" class="text-body-2 text-medium-emphasis">
          Anyone with the link can view and download this folder until it expires.
        </p>
        <v-alert v-if="error" type="error" variant="tonal" class="mt-4" role="alert">
          {{ error }}
        </v-alert>
        <template v-if="link">
          <v-alert type="success" variant="tonal" class="mb-4" role="status">
            Read-only access expires {{ expiryLabel }}.
          </v-alert>
          <v-textarea
            :model-value="link.access_url"
            label="Folder access URL"
            readonly
            variant="outlined"
            rows="3"
            hide-details
            class="access-url"
          />
          <p class="text-body-2 text-medium-emphasis mt-3">
            In Azure Storage Explorer, choose “ADLS Gen2 container or directory”, then “Shared
            access signature (SAS)” and paste this URL.
          </p>
          <p v-if="copyError" class="text-body-2 text-error mt-2" role="alert">
            {{ copyError }}
          </p>
        </template>
      </v-card-text>
      <v-card-actions class="px-6 pb-5 pt-2">
        <v-btn class="text-none" variant="text" @click="dialog = false">Close</v-btn>
        <v-spacer />
        <v-btn
          v-if="link"
          class="text-none"
          color="primary"
          variant="flat"
          :prepend-icon="copied ? 'mdi-check' : 'mdi-content-copy'"
          @click="copyLink"
        >
          {{ copied ? 'Copied' : 'Copy link' }}
        </v-btn>
        <v-btn
          v-else
          class="text-none"
          color="primary"
          variant="flat"
          prepend-icon="mdi-link-variant"
          :loading
          @click="createLink"
        >
          Create link
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<style scoped>
.access-folder {
  align-items: center;
  background: rgba(var(--v-theme-primary), 0.06);
  border-radius: 6px;
  display: flex;
  gap: 10px;
  overflow-wrap: anywhere;
  padding: 12px;
}

.access-url :deep(textarea) {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.8rem;
  overflow-wrap: anywhere;
}
</style>
